// TodayInvoicesGrid.js
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  getDocs
} from "firebase/firestore";
import { DateTime } from "luxon";
import * as XLSX from "xlsx";
import {
  Paper,
  Typography,
  Box,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  Stack,
  useTheme,
  alpha,
  Fade,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Badge,
  Menu,
  Popover,
  Grid,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Download,
  RefreshCw,
  Filter,
  FileSpreadsheet,
  FileText,
  Truck,
  Calendar,
  Store,
  Package,
  AlertCircle,
  CheckCircle,
  XCircle,
  Grid2X2,
  LucideLayoutList,
  TableRowsSplit,
  Settings,
  Save,
  Delete,
  Plus,
  Cross,
  Eye,
  EyeOff,
  SquareArrowUp,
  SquareArrowDown,
  SortAsc,
} from "lucide-react";
import dayjs from "dayjs";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const TodayInvoicesGrid = () => {
  const theme = useTheme();

  // Constants
  const SWEETS_CATEGORY_ID = "IyB2aY8HCpmUQ60GDhue";
  const SAVOURIES_CATEGORY_ID = "u0EKeEirqgp4VWOAVAW3";

  // State for data
  const [data, setData] = useState([]);
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Filter states
  const [selectedRestaurant, setSelectedRestaurant] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [startDate, setStartDate] = useState(dayjs().startOf('day'));
  const [endDate, setEndDate] = useState(dayjs().endOf('day'));
  const [dateRangeType, setDateRangeType] = useState("today");

  // Category states
  const [sweetsCategories, setSweetsCategories] = useState([]);
  const [savouriesCategories, setSavouriesCategories] = useState([]);
  const [categoryMapping, setCategoryMapping] = useState({});
  const [categoryTypeMapping, setCategoryTypeMapping] = useState({});

  // Download states
  const [downloading, setDownloading] = useState(null);

  // View states
  const [viewMode, setViewMode] = useState("grid");

  // Column visibility states
  const [columnSettings, setColumnSettings] = useState({
    showRestaurantName: true,
    showItemName: true,
    showQuantities: true,
    showTotal: true,
    showCategory: true,
  });

  // Sort states
  const [sortConfig, setSortConfig] = useState({ key: 'restaurant', direction: 'asc' });

  // Export preferences
  const [exportSettings, setExportSettings] = useState({
    includeSweets: true,
    includeSavouries: true,
    includeOther: true,
    includeSummaries: true,
    format: 'excel',
    dateFormat: 'DD/MM/YYYY',
    includeTimestamp: true,
  });

  const [showExportSettings, setShowExportSettings] = useState(false);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Selection state for bulk actions
  const [selectedRestaurants, setSelectedRestaurants] = useState([]);

  // Menu anchors
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);

  // Load templates from localStorage on mount
  useEffect(() => {
    const savedTemplates = localStorage.getItem('invoiceGridTemplates');
    if (savedTemplates) {
      setTemplates(JSON.parse(savedTemplates));
    }
  }, []);

  // Fetch categories and identify sweets vs savouries
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const db = getFirestore();
        const categoriesSnapshot = await getDocs(collection(db, "inventoryCategory"));

        const sweets = [];
        const savouries = [];
        const mapping = {};
        const typeMapping = {};

        categoriesSnapshot.forEach((doc) => {
          const categoryData = doc.data();
          const categoryId = doc.id;
          const categoryName = categoryData.category;

          mapping[categoryId] = categoryName;

          if (categoryId === SWEETS_CATEGORY_ID) {
            sweets.push(categoryId);
            typeMapping[categoryId] = 'sweet';
          }
          else if (categoryId === SAVOURIES_CATEGORY_ID) {
            savouries.push(categoryId);
            typeMapping[categoryId] = 'savoury';
          }
          else {
            typeMapping[categoryId] = 'other';
          }
        });

        setSweetsCategories(sweets);
        setSavouriesCategories(savouries);
        setCategoryMapping(mapping);
        setCategoryTypeMapping(typeMapping);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, []);

  const getUKDateRange = () => {
    let start, end;
    const now = DateTime.now().setZone("Europe/London");

    switch (dateRangeType) {
      case "today":
        start = now.startOf("day");
        end = now.endOf("day");
        break;
      case "yesterday":
        start = now.minus({ days: 1 }).startOf("day");
        end = now.minus({ days: 1 }).endOf("day");
        break;
      case "last3days":
        start = now.minus({ days: 3 }).startOf("day");
        end = now.endOf("day");
        break;
      case "week":
        start = now.startOf("week");
        end = now.endOf("day");
        break;
      case "custom":
        start = DateTime.fromISO(startDate.toISOString()).setZone("Europe/London");
        end = DateTime.fromISO(endDate.toISOString()).setZone("Europe/London").endOf("day");
        break;
      default:
        start = now.startOf("day");
        end = now.endOf("day");
    }

    return { start: start.toISO(), end: end.toISO() };
  };

  // Helper to get display-friendly date range based on current filter
  const getDisplayDateRange = () => {
    const now = dayjs();
    switch (dateRangeType) {
      case "today":
        return { displayStart: now.startOf("day"), displayEnd: now.endOf("day") };
      case "yesterday":
        return { displayStart: now.subtract(1, "day").startOf("day"), displayEnd: now.subtract(1, "day").endOf("day") };
      case "last3days":
        return { displayStart: now.subtract(3, "day").startOf("day"), displayEnd: now.endOf("day") };
      case "week":
        return { displayStart: now.startOf("week"), displayEnd: now.endOf("day") };
      case "custom":
        return { displayStart: startDate, displayEnd: endDate };
      default:
        return { displayStart: now.startOf("day"), displayEnd: now.endOf("day") };
    }
  };

  // Filter restaurants based on selected filter type
  const filterRestaurantsByOrders = useCallback((restaurants) => {
    switch (filterType) {
      case "hasOrders":
        return restaurants.filter(r =>
          r.itemQuantities.some(item => item.quantity > 0)
        );
      case "noOrders":
        return restaurants.filter(r =>
          !r.itemQuantities.some(item => item.quantity > 0)
        );
      default:
        return restaurants;
    }
  }, [filterType]);

  // Filter items by category ID
  const filterItemsByCategoryId = useCallback((items, categoryIds) => {
    const filtered = {};

    Object.entries(items).forEach(([categoryName, categoryItems]) => {
      const categoryId = Object.keys(categoryMapping).find(
        id => categoryMapping[id] === categoryName
      );

      if (categoryId && categoryIds.includes(categoryId)) {
        filtered[categoryName] = categoryItems;
      }
    });

    return filtered;
  }, [categoryMapping]);

  // Filter restaurant data for specific category items
  const filterRestaurantDataByCategoryId = useCallback((restaurants, filteredItems) => {
    const allowedItemTitles = new Set();
    Object.values(filteredItems).forEach(categoryItems => {
      categoryItems.forEach(item => allowedItemTitles.add(item.title));
    });

    return restaurants.map(restaurant => ({
      ...restaurant,
      itemQuantities: restaurant.itemQuantities.filter(item =>
        allowedItemTitles.has(item.title)
      )
    })).filter(restaurant => restaurant.itemQuantities.length > 0);
  }, []);

  // Sort data
  const sortData = useCallback((restaurants) => {
    if (!sortConfig.key) return restaurants;

    return [...restaurants].sort((a, b) => {
      let aValue, bValue;

      if (sortConfig.key === 'restaurant') {
        aValue = a.restaurantName;
        bValue = b.restaurantName;
      } else if (sortConfig.key === 'totalOrders') {
        aValue = a.itemQuantities.reduce((sum, item) => sum + item.quantity, 0);
        bValue = b.itemQuantities.reduce((sum, item) => sum + item.quantity, 0);
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortConfig]);

  // Process and filter data
  const processedData = useMemo(() => {
    let filtered = data;

    if (selectedRestaurant !== "all") {
      filtered = filtered.filter(r => r.restaurantName === selectedRestaurant);
    }

    filtered = filterRestaurantsByOrders(filtered);
    filtered = sortData(filtered);

    return filtered;
  }, [data, selectedRestaurant, filterRestaurantsByOrders, sortData]);

  // All categories for UI display
  const allDataForUI = useMemo(() => {
    const allCategories = {};

    Object.entries(items).forEach(([categoryName, categoryItems]) => {
      const categoryId = Object.keys(categoryMapping).find(
        id => categoryMapping[id] === categoryName
      );

      allCategories[categoryName] = {
        items: categoryItems,
        categoryId: categoryId,
        type: categoryId ? categoryTypeMapping[categoryId] || 'other' : 'other'
      };
    });

    return allCategories;
  }, [items, categoryMapping, categoryTypeMapping]);

  const sweetsItems = useMemo(() =>
    filterItemsByCategoryId(items, [SWEETS_CATEGORY_ID]),
    [items, filterItemsByCategoryId]
  );

  const savouriesItems = useMemo(() =>
    filterItemsByCategoryId(items, [SAVOURIES_CATEGORY_ID]),
    [items, filterItemsByCategoryId]
  );

  // Category filtered data for downloads
  const sweetsData = useMemo(() =>
    filterRestaurantDataByCategoryId(processedData, sweetsItems),
    [processedData, sweetsItems, filterRestaurantDataByCategoryId]
  );

  const savouriesData = useMemo(() =>
    filterRestaurantDataByCategoryId(processedData, savouriesItems),
    [processedData, savouriesItems, filterRestaurantDataByCategoryId]
  );

  // Download functions
  const downloadExcel = async () => {
    setDownloading('grid-excel');
    try {
      const workbook = XLSX.utils.book_new();
      const worksheetData = [];

      // Filter out sweets & savouries categories
      const sweetsAndSavouriesCatNames = new Set();
      [SWEETS_CATEGORY_ID, SAVOURIES_CATEGORY_ID].forEach(id => {
        if (categoryMapping[id]) sweetsAndSavouriesCatNames.add(categoryMapping[id]);
      });
      const filteredItems = Object.fromEntries(
        Object.entries(items).filter(([category]) => !sweetsAndSavouriesCatNames.has(category))
      );

      const { displayStart: ds, displayEnd: de } = getDisplayDateRange();
      worksheetData.push([`Complete Invoice Grid`]);
      worksheetData.push([`Date Range: ${ds.format('DD/MM/YYYY')} - ${de.format('DD/MM/YYYY')}`]);
      worksheetData.push([]);
      worksheetData.push(["Item", ...processedData.map(d => d.restaurantName), "Total"]);

      Object.entries(filteredItems).forEach(([category, categoryItems]) => {
        worksheetData.push([category, ...Array(processedData.length + 1).fill("")]);

        categoryItems.forEach(item => {
          const row = [item.title];
          let rowTotal = 0;

          processedData.forEach(restaurant => {
            const quantity = restaurant.itemQuantities.find(q => q.title === item.title)?.quantity || 0;
            row.push(quantity);
            rowTotal += quantity;
          });

          row.push(rowTotal);
          worksheetData.push(row);
        });
      });

      const totalRow = ["Total"];
      let grandTotal = 0;

      processedData.forEach(restaurant => {
        const restaurantTotal = restaurant.itemQuantities.reduce((sum, item) => sum + item.quantity, 0);
        totalRow.push(restaurantTotal);
        grandTotal += restaurantTotal;
      });

      totalRow.push(grandTotal);
      worksheetData.push([]);
      worksheetData.push(totalRow);

      worksheetData.push([]);
      worksheetData.push(["SUMMARY"]);
      worksheetData.push(["Total Categories", Object.keys(filteredItems).length]);
      worksheetData.push(["Total Items", Object.values(filteredItems).flat().length]);
      worksheetData.push(["Total Restaurants", processedData.length]);
      worksheetData.push(["Total Quantity", grandTotal]);
      worksheetData.push(["Generated", new Date().toLocaleString()]);

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      const wsCols = [
        { wch: 30 },
        ...processedData.map(() => ({ wch: 15 })),
        { wch: 15 }
      ];
      worksheet["!cols"] = wsCols;

      XLSX.utils.book_append_sheet(workbook, worksheet, "Complete Grid");
      XLSX.writeFile(workbook, `complete_grid_${new Date().toISOString()}.xlsx`);
    } catch (err) {
      setError("Failed to generate Excel");
      console.error(err);
    } finally {
      setDownloading(null);
    }
  };

  const downloadGridPDF = async () => {
    setDownloading('grid-pdf');
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });

      // Filter out sweets & savouries categories
      const sweetsAndSavouriesCatNames = new Set();
      [SWEETS_CATEGORY_ID, SAVOURIES_CATEGORY_ID].forEach(id => {
        if (categoryMapping[id]) sweetsAndSavouriesCatNames.add(categoryMapping[id]);
      });
      const filteredItems = Object.fromEntries(
        Object.entries(items).filter(([category]) => !sweetsAndSavouriesCatNames.has(category))
      );

      doc.setFontSize(24);
      doc.setTextColor(theme.palette.primary.main);
      doc.text("Complete Invoice Grid", doc.internal.pageSize.getWidth() / 2, 15, { align: "center" });

      doc.setFontSize(10);
      doc.setTextColor(100);
      const { displayStart: pds, displayEnd: pde } = getDisplayDateRange();
      doc.text(`Date Range: ${pds.format('DD/MM/YYYY')} - ${pde.format('DD/MM/YYYY')}`, 10, 25);

      const headers = ["Item", ...processedData.map(d => d.restaurantName), "Total"];
      const rows = [];
      const categoryRowIndices = [];

      Object.entries(filteredItems).forEach(([category, categoryItems]) => {
        categoryRowIndices.push(rows.length);
        rows.push([{ content: category, colSpan: headers.length, styles: { fillColor: [220, 220, 220], fontStyle: 'bold' } }]);

        categoryItems.forEach(item => {
          const row = [item.title];
          let rowTotal = 0;

          processedData.forEach(restaurant => {
            const quantity = restaurant.itemQuantities.find(q => q.title === item.title)?.quantity || 0;
            row.push(quantity);
            rowTotal += quantity;
          });

          row.push(rowTotal);
          rows.push(row);
        });
      });

      const totalRow = ["Total"];
      let grandTotal = 0;

      processedData.forEach(restaurant => {
        const restaurantTotal = restaurant.itemQuantities.reduce((sum, item) => sum + item.quantity, 0);
        totalRow.push(restaurantTotal);
        grandTotal += restaurantTotal;
      });

      totalRow.push(grandTotal);
      rows.push(totalRow);

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 30,
        theme: "grid",
        headStyles: {
          fillColor: [196, 22, 28],
          fontSize: 10,
          fontStyle: "bold",
          textColor: [255, 255, 255],
        },
        bodyStyles: { fontSize: 10, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 249, 250] },
      });

      doc.addPage();
      doc.setFontSize(16);
      doc.setTextColor(theme.palette.primary.main);
      doc.text("Summary", 14, 20);

      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text(`Total Categories: ${Object.keys(filteredItems).length}`, 14, 40);
      doc.text(`Total Items: ${Object.values(filteredItems).flat().length}`, 14, 50);
      doc.text(`Total Restaurants: ${processedData.length}`, 14, 60);
      doc.text(`Total Quantity: ${grandTotal}`, 14, 70);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 80);

      doc.save(`complete_grid_${new Date().toISOString()}.pdf`);
    } catch (err) {
      setError("Failed to generate PDF");
      console.error(err);
    } finally {
      setDownloading(null);
    }
  };

  const downloadDispatchNotesExcel = async () => {
    setDownloading('dispatch-excel');
    try {
      const workbook = XLSX.utils.book_new();

      processedData.forEach((restaurant) => {
        const sheetData = [];
        const { restaurantName, itemQuantities } = restaurant;

        const itemsWithQuantity = itemQuantities.filter(i => i.quantity > 0);
        if (itemsWithQuantity.length === 0) return;

        const { displayStart: dds } = getDisplayDateRange();
        sheetData.push(['Madras Flavours Kitchen Limited']);
        sheetData.push([`DISPATCH NOTE - ${restaurantName}`]);
        sheetData.push([`Order Date: ${dds.format('DD/MM/YYYY')}`, `Delivery Date: __________`]);
        sheetData.push([`Vehicle No: __________`, `Driver Name: __________`, `Vehicle Temp: __________`]);
        sheetData.push([]);
        sheetData.push(["S.No", "Product Name", "Quantity", "Batch No", "Batch No", "Remarks"]);

        let serial = 1;
        let totalQuantity = 0;

        itemsWithQuantity.forEach((item) => {
          const qty = Number(item.quantity) || 0;
          totalQuantity += qty;
          const code1 = item.batchCodes?.[0] || "";
          const code2 = item.batchCodes?.[1] || "";
          sheetData.push([serial, item.title, qty, code1, code2, ""]);
          serial += 1;
        });

        sheetData.push([]);
        sheetData.push(["TOTAL QUANTITY", "", totalQuantity, "", "", ""]);
        sheetData.push([]);
        sheetData.push(["Driver Signature: _________________", "", "Receiver Signature: _________________"]);

        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
        const wsCols = [
          { wch: 6 }, { wch: 40 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 30 }
        ];
        worksheet["!cols"] = wsCols;

        let safeName = restaurantName.substring(0, 31).replace(/[\\/?*\[\]:]/g, " ");
        XLSX.utils.book_append_sheet(workbook, worksheet, safeName);
      });

      XLSX.writeFile(workbook, `dispatch_notes_complete_${new Date().toISOString()}.xlsx`);
    } catch (err) {
      setError("Failed to generate dispatch notes Excel");
      console.error(err);
    } finally {
      setDownloading(null);
    }
  };

  const downloadDispatchNotesPDF = async () => {
    setDownloading('dispatch-pdf');
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      let isFirstPage = true;

      processedData.forEach((restaurant) => {
        const { restaurantName, itemQuantities } = restaurant;

        const itemsWithQuantity = itemQuantities.filter(i => i.quantity > 0);
        if (itemsWithQuantity.length === 0) return;

        if (!isFirstPage) doc.addPage();
        isFirstPage = false;

        const tableRows = [];

        tableRows.push([
          {
            content: 'Madras Flavours Kitchen Limited', colSpan: 6, styles: {
              fontSize: 16,
              fontStyle: "bold",
              halign: "center",
              textColor: [0, 0, 0],
            }
          },
        ]);

        tableRows.push([
          {
            content: `DISPATCH NOTE - ${restaurantName}`, colSpan: 6, styles: {
              fontSize: 14,
              fontStyle: "bold",
              fillColor: [196, 22, 28],
              textColor: [255, 255, 255],
              halign: "center"
            }
          },
        ]);

        const { displayStart: dpds } = getDisplayDateRange();
        tableRows.push([
          { content: `Order Date: ${dpds.format('DD/MM/YYYY')}`, colSpan: 2 },
          { content: `Delivery Date: ________`, colSpan: 2 },
          { content: `Complete Order`, colSpan: 2 },
        ]);

        tableRows.push([
          { content: `Vehicle No: ________`, colSpan: 2 },
          { content: `Driver Name: ________`, colSpan: 2 },
          { content: `Vehicle Temp: ________`, colSpan: 2 },
        ]);

        tableRows.push([]);

        tableRows.push([
          { content: "S.No", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
          { content: "Product", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
          { content: "Quantity", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
          { content: "Batch No", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
          { content: "Batch No", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
          { content: "Remarks", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
        ]);

        let serial = 1;
        let totalQuantity = 0;

        itemsWithQuantity.forEach((item) => {
          const qty = Number(item.quantity) || 0;
          totalQuantity += qty;
          const code1 = item.batchCodes?.[0] || "";
          const code2 = item.batchCodes?.[1] || "";

          tableRows.push([
            { content: serial.toString() },
            { content: item.title },
            { content: qty.toString() },
            { content: code1 },
            { content: code2 },
            { content: "" },
          ]);
          serial += 1;
        });

        tableRows.push([]);
        tableRows.push([
          { content: "", colSpan: 2 },
          { content: `Total Quantity: ${totalQuantity}`, colSpan: 4, styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
        ]);

        tableRows.push([]);
        tableRows.push([
          { content: "Driver Signature: _________________", colSpan: 3 },
          { content: "Receiver Signature: _________________", colSpan: 3 },
        ]);

        autoTable(doc, {
          body: tableRows,
          startY: 10,
          theme: "grid",
          styles: { fontSize: 8, cellPadding: 3 },
          columnStyles: {
            0: { cellWidth: 15 },
            1: { cellWidth: 60 },
            2: { cellWidth: 20 },
            3: { cellWidth: 25 },
            4: { cellWidth: 25 },
            5: { cellWidth: 35 },
          },
        });
      });

      doc.save(`dispatch_notes_complete_${new Date().toISOString()}.pdf`);
    } catch (err) {
      setError("Failed to generate dispatch notes PDF");
      console.error(err);
    } finally {
      setDownloading(null);
    }
  };

  // const downloadCategoryExcel = async (category, categoryName, categoryData, categoryItems) => {
  //   setDownloading(`${category}-excel`);
  //   try {
  //     const workbook = XLSX.utils.book_new();
  //     const worksheetData = [];

  //     worksheetData.push([`${categoryName} Items`]);
  //     worksheetData.push([`Date Range: ${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`]);
  //     worksheetData.push([]);
  //     worksheetData.push(["Item", ...categoryData.map(d => d.restaurantName), "Total"]);

  //     Object.entries(categoryItems).forEach(([catName, catItems]) => {
  //       worksheetData.push([catName, ...Array(categoryData.length + 1).fill("")]);

  //       catItems.forEach(item => {
  //         const row = [item.title];
  //         let rowTotal = 0;

  //         categoryData.forEach(restaurant => {
  //           const quantity = restaurant.itemQuantities.find(q => q.title === item.title)?.quantity || 0;
  //           row.push(quantity);
  //           rowTotal += quantity;
  //         });

  //         row.push(rowTotal);
  //         worksheetData.push(row);
  //       });
  //     });

  //     const totalRow = ["Total"];
  //     let grandTotal = 0;

  //     categoryData.forEach(restaurant => {
  //       const restaurantTotal = restaurant.itemQuantities.reduce((sum, item) => sum + item.quantity, 0);
  //       totalRow.push(restaurantTotal);
  //       grandTotal += restaurantTotal;
  //     });

  //     totalRow.push(grandTotal);
  //     worksheetData.push([]);
  //     worksheetData.push(totalRow);

  //     if (exportSettings.includeSummaries) {
  //       worksheetData.push([]);
  //       worksheetData.push(["SUMMARY"]);
  //       worksheetData.push(["Total Items", Object.values(categoryItems).flat().length]);
  //       worksheetData.push(["Total Restaurants", categoryData.length]);
  //       worksheetData.push(["Total Quantity", grandTotal]);
  //       worksheetData.push(["Generated", new Date().toLocaleString()]);
  //     }

  //     const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  //     const wsCols = [
  //       { wch: 30 },
  //       ...categoryData.map(() => ({ wch: 15 })),
  //       { wch: 15 }
  //     ];
  //     worksheet["!cols"] = wsCols;

  //     XLSX.utils.book_append_sheet(workbook, worksheet, categoryName);
  //     XLSX.writeFile(workbook, `${categoryName.toLowerCase()}_${new Date().toISOString()}.xlsx`);
  //   } catch (err) {
  //     setError(`Failed to generate ${categoryName} Excel`);
  //     console.error(err);
  //   } finally {
  //     setDownloading(null);
  //   }
  // };

  // const downloadCategoryPDF = async (category, categoryName, categoryData, categoryItems) => {
  //   setDownloading(`${category}-pdf`);
  //   try {
  //     const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });

  //     doc.setFontSize(24);
  //     doc.setTextColor(theme.palette.primary.main);
  //     doc.text(`${categoryName} Items`, doc.internal.pageSize.getWidth() / 2, 15, { align: "center" });

  //     doc.setFontSize(10);
  //     doc.setTextColor(100);
  //     doc.text(`Date Range: ${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`, 10, 25);

  //     const headers = ["Item", ...categoryData.map(d => d.restaurantName), "Total"];
  //     const rows = [];

  //     Object.entries(categoryItems).forEach(([catName, catItems]) => {
  //       rows.push([catName, ...Array(headers.length - 1).fill("")]);

  //       catItems.forEach(item => {
  //         const row = [item.title];
  //         let rowTotal = 0;

  //         categoryData.forEach(restaurant => {
  //           const quantity = restaurant.itemQuantities.find(q => q.title === item.title)?.quantity || 0;
  //           row.push(quantity);
  //           rowTotal += quantity;
  //         });

  //         row.push(rowTotal);
  //         rows.push(row);
  //       });
  //     });

  //     const totalRow = ["Total"];
  //     let grandTotal = 0;

  //     categoryData.forEach(restaurant => {
  //       const restaurantTotal = restaurant.itemQuantities.reduce((sum, item) => sum + item.quantity, 0);
  //       totalRow.push(restaurantTotal);
  //       grandTotal += restaurantTotal;
  //     });

  //     totalRow.push(grandTotal);
  //     rows.push(totalRow);

  //     autoTable(doc, {
  //       head: [headers],
  //       body: rows,
  //       startY: 30,
  //       theme: "grid",
  //       headStyles: {
  //         fillColor: [196, 22, 28],
  //         fontSize: 10,
  //         fontStyle: "bold",
  //         textColor: [255, 255, 255],
  //       },
  //       bodyStyles: { fontSize: 9 },
  //       alternateRowStyles: { fillColor: [248, 249, 250] },
  //     });

  //     if (exportSettings.includeSummaries) {
  //       doc.addPage();
  //       doc.setFontSize(16);
  //       doc.setTextColor(theme.palette.primary.main);
  //       doc.text("Summary", 14, 20);

  //       doc.setFontSize(12);
  //       doc.setTextColor(0);
  //       doc.text(`Total Items: ${Object.values(categoryItems).flat().length}`, 14, 40);
  //       doc.text(`Total Restaurants: ${categoryData.length}`, 14, 50);
  //       doc.text(`Total Quantity: ${grandTotal}`, 14, 60);
  //       doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 70);
  //     }

  //     doc.save(`${categoryName.toLowerCase()}_${new Date().toISOString()}.pdf`);
  //   } catch (err) {
  //     setError(`Failed to generate ${categoryName} PDF`);
  //     console.error(err);
  //   } finally {
  //     setDownloading(null);
  //   }
  // };

  // const downloadCategoryDispatchExcel = async (category, categoryName, categoryData, categoryItems) => {
  //   setDownloading(`${category}-dispatch-excel`);
  //   try {
  //     const workbook = XLSX.utils.book_new();

  //     categoryData.forEach((restaurant) => {
  //       const { restaurantName, itemQuantities } = restaurant;

  //       const categoryItemTitles = new Set(Object.values(categoryItems).flat().map(i => i.title));
  //       const itemsWithQuantity = itemQuantities.filter(i => i.quantity > 0 && categoryItemTitles.has(i.title));

  //       if (itemsWithQuantity.length === 0) return;

  //       const sheetData = [];

  //       sheetData.push([`DISPATCH NOTE - ${restaurantName}`]);
  //       sheetData.push([`Order Date: ${startDate.format('DD/MM/YYYY')}`, `Delivery Date: __________`]);
  //       sheetData.push([`Vehicle No: __________`, `Driver Name: __________`, `Vehicle Temp: __________`]);
  //       sheetData.push([`Category: ${categoryName}`]);
  //       sheetData.push([]);
  //       sheetData.push(["S.No", "Product Name", "Quantity", "Batch No", "Batch No", "Remarks"]);

  //       let serial = 1;
  //       let totalQuantity = 0;

  //       itemsWithQuantity.forEach((item) => {
  //         const qty = Number(item.quantity) || 0;
  //         totalQuantity += qty;
  //         sheetData.push([serial, item.title, qty, "", "", ""]);
  //         serial += 1;
  //       });

  //       sheetData.push([]);
  //       sheetData.push(["TOTAL QUANTITY", "", totalQuantity, "", "", ""]);
  //       sheetData.push([]);
  //       sheetData.push(["Driver Signature: _________________", "", "Receiver Signature: _________________"]);

  //       const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  //       const wsCols = [
  //         { wch: 6 }, { wch: 40 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 30 }
  //       ];
  //       worksheet["!cols"] = wsCols;

  //       let safeName = `${categoryName}_${restaurantName.substring(0, 20)}`.replace(/[\\/?*\[\]:]/g, " ");
  //       XLSX.utils.book_append_sheet(workbook, worksheet, safeName);
  //     });

  //     XLSX.writeFile(workbook, `${categoryName.toLowerCase()}_dispatch_${new Date().toISOString()}.xlsx`);
  //   } catch (err) {
  //     setError(`Failed to generate ${categoryName} dispatch Excel`);
  //     console.error(err);
  //   } finally {
  //     setDownloading(null);
  //   }
  // };

  // const downloadCategoryDispatchPDF = async (category, categoryName, categoryData, categoryItems) => {
  //   setDownloading(`${category}-dispatch-pdf`);
  //   try {
  //     const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  //     let isFirstPage = true;

  //     categoryData.forEach((restaurant) => {
  //       const { restaurantName, itemQuantities } = restaurant;

  //       const categoryItemTitles = new Set(Object.values(categoryItems).flat().map(i => i.title));
  //       const itemsWithQuantity = itemQuantities.filter(i => i.quantity > 0 && categoryItemTitles.has(i.title));

  //       if (itemsWithQuantity.length === 0) return;

  //       if (!isFirstPage) doc.addPage();
  //       isFirstPage = false;

  //       const tableRows = [];

  //       tableRows.push([
  //         {
  //           content: `DISPATCH NOTE - ${restaurantName}`, colSpan: 6, styles: {
  //             fontSize: 14,
  //             fontStyle: "bold",
  //             fillColor: [196, 22, 28],
  //             textColor: [255, 255, 255],
  //             halign: "center"
  //           }
  //         },
  //       ]);

  //       tableRows.push([
  //         { content: `Order Date: ${startDate.format('DD/MM/YYYY')}`, colSpan: 2 },
  //         { content: `Delivery Date: ________`, colSpan: 2 },
  //         { content: `Category: ${categoryName}`, colSpan: 2 },
  //       ]);

  //       tableRows.push([
  //         { content: `Vehicle No: ________`, colSpan: 2 },
  //         { content: `Driver Name: ________`, colSpan: 2 },
  //         { content: `Vehicle Temp: ________`, colSpan: 2 },
  //       ]);

  //       tableRows.push([]);

  //       tableRows.push([
  //         { content: "S.No", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
  //         { content: "Product", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
  //         { content: "Quantity", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
  //         { content: "Batch No", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
  //         { content: "Batch No", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
  //         { content: "Remarks", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
  //       ]);

  //       let serial = 1;
  //       let totalQuantity = 0;

  //       itemsWithQuantity.forEach((item) => {
  //         const qty = Number(item.quantity) || 0;
  //         totalQuantity += qty;

  //         tableRows.push([
  //           { content: serial.toString() },
  //           { content: item.title },
  //           { content: qty.toString() },
  //           { content: "" },
  //           { content: "" },
  //           { content: "" },
  //         ]);
  //         serial += 1;
  //       });

  //       tableRows.push([]);
  //       tableRows.push([
  //         { content: "", colSpan: 2 },
  //         { content: `Total Quantity: ${totalQuantity}`, colSpan: 4, styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
  //       ]);

  //       tableRows.push([]);
  //       tableRows.push([
  //         { content: "Driver Signature: _________________", colSpan: 3 },
  //         { content: "Receiver Signature: _________________", colSpan: 3 },
  //       ]);

  //       autoTable(doc, {
  //         body: tableRows,
  //         startY: 10,
  //         theme: "grid",
  //         styles: { fontSize: 8, cellPadding: 3 },
  //         columnStyles: {
  //           0: { cellWidth: 15 },
  //           1: { cellWidth: 60 },
  //           2: { cellWidth: 20 },
  //           3: { cellWidth: 25 },
  //           4: { cellWidth: 25 },
  //           5: { cellWidth: 35 },
  //         },
  //       });
  //     });

  //     doc.save(`${categoryName.toLowerCase()}_dispatch_${new Date().toISOString()}.pdf`);
  //   } catch (err) {
  //     setError(`Failed to generate ${categoryName} dispatch PDF`);
  //     console.error(err);
  //   } finally {
  //     setDownloading(null);
  //   }
  // };

  // 1. Combined Sweets & Savouries Excel Grid
  const downloadCombinedExcel = async () => {
    setDownloading('combined-excel');
    try {
      // Combine both categories
      const combinedItems = { ...sweetsItems, ...savouriesItems };
      const combinedData = [...sweetsData, ...savouriesData];

      // Remove duplicates by restaurant name
      const uniqueRestaurants = {};
      combinedData.forEach(restaurant => {
        if (!uniqueRestaurants[restaurant.restaurantName]) {
          uniqueRestaurants[restaurant.restaurantName] = { ...restaurant };
        } else {
          restaurant.itemQuantities.forEach(item => {
            const existingItem = uniqueRestaurants[restaurant.restaurantName].itemQuantities
              .find(i => i.title === item.title);
            if (existingItem) {
              existingItem.quantity += item.quantity;
            } else {
              uniqueRestaurants[restaurant.restaurantName].itemQuantities.push({ ...item });
            }
          });
        }
      });

      const combinedUniqueData = Object.values(uniqueRestaurants);

      const workbook = XLSX.utils.book_new();
      const worksheetData = [];

      const { displayStart: csds, displayEnd: csde } = getDisplayDateRange();
      worksheetData.push([`Sweets & Savouries Grid`]);
      worksheetData.push([`Date Range: ${csds.format('DD/MM/YYYY')} - ${csde.format('DD/MM/YYYY')}`]);
      worksheetData.push([]);
      worksheetData.push(["Item", ...combinedUniqueData.map(d => d.restaurantName), "Total"]);

      // Track category header rows for styling
      const categoryRowIndices = [];

      // Sweets Section - only if sweets items exist
      const hasSweetsItems = Object.keys(sweetsItems).length > 0;
      if (hasSweetsItems) {
        Object.entries(sweetsItems).forEach(([catName, catItems]) => {
          categoryRowIndices.push(worksheetData.length);
          worksheetData.push([catName, ...Array(combinedUniqueData.length + 1).fill("")]);
          catItems.forEach(item => {
            const row = [item.title];
            let rowTotal = 0;
            combinedUniqueData.forEach(restaurant => {
              const quantity = restaurant.itemQuantities.find(q => q.title === item.title)?.quantity || 0;
              row.push(quantity);
              rowTotal += quantity;
            });
            row.push(rowTotal);
            worksheetData.push(row);
          });
        });
      }

      // Savouries Section - only if savouries items exist
      const hasSavouriesItems = Object.keys(savouriesItems).length > 0;
      if (hasSavouriesItems) {
        Object.entries(savouriesItems).forEach(([catName, catItems]) => {
          categoryRowIndices.push(worksheetData.length);
          worksheetData.push([catName, ...Array(combinedUniqueData.length + 1).fill("")]);
          catItems.forEach(item => {
            const row = [item.title];
            let rowTotal = 0;
            combinedUniqueData.forEach(restaurant => {
              const quantity = restaurant.itemQuantities.find(q => q.title === item.title)?.quantity || 0;
              row.push(quantity);
              rowTotal += quantity;
            });
            row.push(rowTotal);
            worksheetData.push(row);
          });
        });
      }

      // Totals
      const totalRow = ["GRAND TOTAL"];
      let grandTotal = 0;
      combinedUniqueData.forEach(restaurant => {
        const restaurantTotal = restaurant.itemQuantities.reduce((sum, item) => sum + item.quantity, 0);
        totalRow.push(restaurantTotal);
        grandTotal += restaurantTotal;
      });
      totalRow.push(grandTotal);
      worksheetData.push([]);
      worksheetData.push(totalRow);

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const wsCols = [
        { wch: 40 },
        ...combinedUniqueData.map(() => ({ wch: 15 })),
        { wch: 15 }
      ];
      worksheet["!cols"] = wsCols;

      XLSX.utils.book_append_sheet(workbook, worksheet, "Sweets & Savouries");
      XLSX.writeFile(workbook, `sweets_savouries_combined_${new Date().toISOString()}.xlsx`);
    } catch (err) {
      setError("Failed to generate combined Excel");
      console.error(err);
    } finally {
      setDownloading(null);
    }
  };

  // 2. Combined Sweets & Savouries PDF Grid
  const downloadCombinedPDF = async () => {
    setDownloading('combined-pdf');
    try {
      // Combine both categories
      const combinedItems = { ...sweetsItems, ...savouriesItems };
      const combinedData = [...sweetsData, ...savouriesData];

      // Remove duplicates by restaurant name
      const uniqueRestaurants = {};
      combinedData.forEach(restaurant => {
        if (!uniqueRestaurants[restaurant.restaurantName]) {
          uniqueRestaurants[restaurant.restaurantName] = { ...restaurant };
        } else {
          restaurant.itemQuantities.forEach(item => {
            const existingItem = uniqueRestaurants[restaurant.restaurantName].itemQuantities
              .find(i => i.title === item.title);
            if (existingItem) {
              existingItem.quantity += item.quantity;
            } else {
              uniqueRestaurants[restaurant.restaurantName].itemQuantities.push({ ...item });
            }
          });
        }
      });

      const combinedUniqueData = Object.values(uniqueRestaurants);

      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });

      doc.setFontSize(24);
      doc.setTextColor(theme.palette.primary.main);
      doc.text("Sweets & Savouries Grid", doc.internal.pageSize.getWidth() / 2, 15, { align: "center" });

      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Date Range: ${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`, 10, 25);

      const headers = ["Item", ...combinedUniqueData.map(d => d.restaurantName), "Total"];
      const rows = [];

      // Track category row indices for styling
      const categoryRowIndices = [];

      // Sweets Section - only if items exist
      const hasSweetsItems = Object.keys(sweetsItems).length > 0;
      if (hasSweetsItems) {
        Object.entries(sweetsItems).forEach(([catName, catItems]) => {
          categoryRowIndices.push(rows.length);
          rows.push([{ content: catName, colSpan: headers.length, styles: { fillColor: [220, 220, 220], fontStyle: 'bold' } }]);
          catItems.forEach(item => {
            const row = [item.title];
            let rowTotal = 0;
            combinedUniqueData.forEach(restaurant => {
              const quantity = restaurant.itemQuantities.find(q => q.title === item.title)?.quantity || 0;
              row.push(quantity);
              rowTotal += quantity;
            });
            row.push(rowTotal);
            rows.push(row);
          });
        });
      }

      // Savouries Section - only if items exist
      const hasSavouriesItems = Object.keys(savouriesItems).length > 0;
      if (hasSavouriesItems) {
        Object.entries(savouriesItems).forEach(([catName, catItems]) => {
          categoryRowIndices.push(rows.length);
          rows.push([{ content: catName, colSpan: headers.length, styles: { fillColor: [220, 220, 220], fontStyle: 'bold' } }]);
          catItems.forEach(item => {
            const row = [item.title];
            let rowTotal = 0;
            combinedUniqueData.forEach(restaurant => {
              const quantity = restaurant.itemQuantities.find(q => q.title === item.title)?.quantity || 0;
              row.push(quantity);
              rowTotal += quantity;
            });
            row.push(rowTotal);
            rows.push(row);
          });
        });
      }

      const totalRow = ["GRAND TOTAL"];
      let grandTotal = 0;
      combinedUniqueData.forEach(restaurant => {
        const restaurantTotal = restaurant.itemQuantities.reduce((sum, item) => sum + item.quantity, 0);
        totalRow.push(restaurantTotal);
        grandTotal += restaurantTotal;
      });
      totalRow.push(grandTotal);
      rows.push(totalRow);

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 30,
        theme: "grid",
        headStyles: {
          fillColor: [196, 22, 28],
          fontSize: 10,
          fontStyle: "bold",
          textColor: [255, 255, 255],
        },
        bodyStyles: { fontSize: 10, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 249, 250] },
      });

      doc.save(`sweets_savouries_combined_${new Date().toISOString()}.pdf`);
    } catch (err) {
      setError("Failed to generate combined PDF");
      console.error(err);
    } finally {
      setDownloading(null);
    }
  };

  // 3. Combined Sweets & Savouries Dispatch Excel
  const downloadCombinedDispatchExcel = async () => {
    setDownloading('combined-dispatch-excel');
    try {
      // Combine both categories
      const combinedData = [...sweetsData, ...savouriesData];

      // Remove duplicates by restaurant name
      const uniqueRestaurants = {};
      combinedData.forEach(restaurant => {
        if (!uniqueRestaurants[restaurant.restaurantName]) {
          uniqueRestaurants[restaurant.restaurantName] = {
            ...restaurant,
            itemQuantities: restaurant.itemQuantities.map(i => ({ ...i }))
          };
        } else {
          restaurant.itemQuantities.forEach(item => {
            const existingItem = uniqueRestaurants[restaurant.restaurantName].itemQuantities
              .find(i => i.title === item.title);
            if (existingItem) {
              existingItem.quantity += item.quantity;
              if (Array.isArray(item.batchCodes)) {
                if (!existingItem.batchCodes) existingItem.batchCodes = [];
                item.batchCodes.forEach(code => {
                  if (code && !existingItem.batchCodes.includes(code)) {
                    existingItem.batchCodes.push(code);
                  }
                });
              }
            } else {
              uniqueRestaurants[restaurant.restaurantName].itemQuantities.push({ ...item });
            }
          });
        }
      });

      const combinedUniqueData = Object.values(uniqueRestaurants);

      const workbook = XLSX.utils.book_new();

      combinedUniqueData.forEach((restaurant) => {
        const { restaurantName, itemQuantities } = restaurant;
        const itemsWithQuantity = itemQuantities.filter(i => i.quantity > 0);
        if (itemsWithQuantity.length === 0) return;

        const sheetData = [];

        sheetData.push(['Madras Flavours Kitchen Limited']);
        sheetData.push([`DISPATCH NOTE - ${restaurantName}`]);
        sheetData.push([`Order Date: ${startDate.format('DD/MM/YYYY')}`, `Delivery Date: __________`]);
        sheetData.push([`Vehicle No: __________`, `Driver Name: __________`, `Vehicle Temp: __________`]);
        sheetData.push([`Category: Sweets & Savouries Combined`]);
        sheetData.push([]);
        sheetData.push(["S.No", "Product Name", "Quantity", "Batch No", "Batch No", "Remarks"]);

        let serial = 1;
        let totalQuantity = 0;

        itemsWithQuantity.forEach((item) => {
          const qty = Number(item.quantity) || 0;
          totalQuantity += qty;
          const code1 = item.batchCodes?.[0] || "";
          const code2 = item.batchCodes?.[1] || "";
          sheetData.push([serial, item.title, qty, code1, code2, ""]);
          serial += 1;
        });

        sheetData.push([]);
        sheetData.push(["TOTAL QUANTITY", "", totalQuantity, "", "", ""]);
        sheetData.push([]);
        sheetData.push(["Driver Signature: _________________", "", "Receiver Signature: _________________"]);

        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
        const wsCols = [
          { wch: 6 }, { wch: 50 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 30 }
        ];
        worksheet["!cols"] = wsCols;

        let safeName = `Combined_${restaurantName.substring(0, 20)}`.replace(/[\\/?*\[\]:]/g, " ");
        XLSX.utils.book_append_sheet(workbook, worksheet, safeName);
      });

      XLSX.writeFile(workbook, `combined_dispatch_${new Date().toISOString()}.xlsx`);
    } catch (err) {
      setError("Failed to generate combined dispatch Excel");
      console.error(err);
    } finally {
      setDownloading(null);
    }
  };

  // 4. Combined Sweets & Savouries Dispatch PDF
  const downloadCombinedDispatchPDF = async () => {
    setDownloading('combined-dispatch-pdf');
    try {
      // Combine both categories
      const combinedData = [...sweetsData, ...savouriesData];

      // Remove duplicates by restaurant name
      const uniqueRestaurants = {};
      combinedData.forEach(restaurant => {
        if (!uniqueRestaurants[restaurant.restaurantName]) {
          uniqueRestaurants[restaurant.restaurantName] = {
            ...restaurant,
            itemQuantities: restaurant.itemQuantities.map(i => ({ ...i }))
          };
        } else {
          restaurant.itemQuantities.forEach(item => {
            const existingItem = uniqueRestaurants[restaurant.restaurantName].itemQuantities
              .find(i => i.title === item.title);
            if (existingItem) {
              existingItem.quantity += item.quantity;
              if (Array.isArray(item.batchCodes)) {
                if (!existingItem.batchCodes) existingItem.batchCodes = [];
                item.batchCodes.forEach(code => {
                  if (code && !existingItem.batchCodes.includes(code)) {
                    existingItem.batchCodes.push(code);
                  }
                });
              }
            } else {
              uniqueRestaurants[restaurant.restaurantName].itemQuantities.push({ ...item });
            }
          });
        }
      });

      const combinedUniqueData = Object.values(uniqueRestaurants);

      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      let isFirstPage = true;

      combinedUniqueData.forEach((restaurant) => {
        const { restaurantName, itemQuantities } = restaurant;
        const itemsWithQuantity = itemQuantities.filter(i => i.quantity > 0);

        if (itemsWithQuantity.length === 0) return;

        if (!isFirstPage) doc.addPage();
        isFirstPage = false;

        const tableRows = [];

        tableRows.push([
          {
            content: 'Madras Flavours Kitchen Limited', colSpan: 6, styles: {
              fontSize: 16,
              fontStyle: "bold",
              halign: "center",
              textColor: [0, 0, 0],
            }
          },
        ]);

        tableRows.push([
          {
            content: `DISPATCH NOTE - ${restaurantName}`, colSpan: 6, styles: {
              fontSize: 14,
              fontStyle: "bold",
              fillColor: [196, 22, 28],
              textColor: [255, 255, 255],
              halign: "center"
            }
          },
        ]);

        tableRows.push([
          { content: `Order Date: ${startDate.format('DD/MM/YYYY')}`, colSpan: 2 },
          { content: `Delivery Date: ________`, colSpan: 2 },
          { content: `Sweets & Savouries Combined`, colSpan: 2 },
        ]);

        tableRows.push([
          { content: `Vehicle No: ________`, colSpan: 2 },
          { content: `Driver Name: ________`, colSpan: 2 },
          { content: `Vehicle Temp: ________`, colSpan: 2 },
        ]);

        tableRows.push([]);

        tableRows.push([
          { content: "S.No", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
          { content: "Product", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
          { content: "Quantity", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
          { content: "Batch No", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
          { content: "Batch No", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
          { content: "Remarks", styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
        ]);

        let serial = 1;
        let totalQuantity = 0;

        itemsWithQuantity.forEach((item) => {
          const qty = Number(item.quantity) || 0;
          totalQuantity += qty;
          const code1 = item.batchCodes?.[0] || "";
          const code2 = item.batchCodes?.[1] || "";

          tableRows.push([
            { content: serial.toString() },
            { content: item.title },
            { content: qty.toString() },
            { content: code1 },
            { content: code2 },
            { content: "" },
          ]);
          serial += 1;
        });

        tableRows.push([]);
        tableRows.push([
          { content: "", colSpan: 2 },
          { content: `Total Quantity: ${totalQuantity}`, colSpan: 4, styles: { fontStyle: "bold", fillColor: [248, 249, 250] } },
        ]);

        tableRows.push([]);
        tableRows.push([
          { content: "Driver Signature: _________________", colSpan: 3 },
          { content: "Receiver Signature: _________________", colSpan: 3 },
        ]);

        autoTable(doc, {
          body: tableRows,
          startY: 10,
          theme: "grid",
          styles: { fontSize: 8, cellPadding: 3 },
          columnStyles: {
            0: { cellWidth: 15 },
            1: { cellWidth: 70 },
            2: { cellWidth: 20 },
            3: { cellWidth: 25 },
            4: { cellWidth: 25 },
            5: { cellWidth: 35 },
          },
        });
      });

      doc.save(`combined_dispatch_${new Date().toISOString()}.pdf`);
    } catch (err) {
      setError("Failed to generate combined dispatch PDF");
      console.error(err);
    } finally {
      setDownloading(null);
    }
  };


  // Save template
  const saveTemplate = () => {
    if (!templateName.trim()) return;

    const newTemplate = {
      id: Date.now(),
      name: templateName,
      settings: exportSettings,
      dateRangeType,
      filterType,
      columnSettings,
    };

    const updatedTemplates = [...templates, newTemplate];
    setTemplates(updatedTemplates);
    localStorage.setItem('invoiceGridTemplates', JSON.stringify(updatedTemplates));
    setTemplateName("");
    setSavingTemplate(false);
  };

  // Load template
  const loadTemplate = (template) => {
    setExportSettings(template.settings);
    setDateRangeType(template.dateRangeType);
    setFilterType(template.filterType);
    setColumnSettings(template.columnSettings);
    setShowTemplates(false);
  };

  // Delete template
  const deleteTemplate = (id) => {
    const updatedTemplates = templates.filter(t => t.id !== id);
    setTemplates(updatedTemplates);
    localStorage.setItem('invoiceGridTemplates', JSON.stringify(updatedTemplates));
  };

  // Handle select all restaurants
  const handleSelectAll = () => {
    if (selectedRestaurants.length === processedData.length) {
      setSelectedRestaurants([]);
    } else {
      setSelectedRestaurants(processedData.map(r => r.restaurantName));
    }
  };

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setSortAnchorEl(null);
  };

  const DownloadButton = ({ onClick, icon, label, loading, variant = "contained", color = "primary", size = "medium" }) => (
    <Button
      variant={variant}
      onClick={onClick}
      disabled={loading}
      size={size}
      startIcon={loading ? <CircularProgress size={16} /> : icon}
      sx={{
        borderRadius: 2,
        textTransform: 'none',
        fontWeight: 500,
        transition: 'all 0.2s',
        '&:hover': { transform: 'translateY(-2px)' },
      }}
    >
      {label}
    </Button>
  );

  // Style helpers
  const getHeaderStyle = (isSticky = false) => ({
    position: isSticky ? "sticky" : "relative",
    left: isSticky ? 0 : "auto",
    zIndex: isSticky ? 10 : 5, // Increased z-index for better stacking
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: viewMode === 'compact' ? "8px" : "12px",
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    fontSize: "0.875rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    minWidth: isSticky ? 180 : 120, // Slightly wider for better readability
    boxShadow: isSticky ? '2px 0 5px rgba(0,0,0,0.1)' : 'none', // Add shadow to sticky column
  });

  const getCellStyle = (isSticky = false, hasValue = false) => ({
    position: isSticky ? "sticky" : "relative",
    left: isSticky ? 0 : "auto",
    zIndex: isSticky ? 5 : 1,
    padding: viewMode === 'compact' ? "8px" : "12px",
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    backgroundColor: hasValue ? alpha(theme.palette.success.main, 0.02) : "transparent",
    textAlign: "center",
    minWidth: isSticky ? 180 : 100,
    whiteSpace: "nowrap",
    boxShadow: isSticky ? '2px 0 5px rgba(0,0,0,0.05)' : 'none',
  });

  const getCategoryStyle = () => ({
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    fontWeight: 600,
    padding: "8px 12px",
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    color: theme.palette.text.primary,
  });

  const getTotalCellStyle = () => ({
    position: "sticky",
    right: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 8,
    minWidth: 120,
    boxShadow: '-2px 0 5px rgba(0,0,0,0.05)',
  });

  // Render different view modes
  const renderGridView = () => (
    <Box
      sx={{
        maxWidth: '80vw',  // Changed from '100%' to viewport width
        position: 'relative',
        overflow: 'hidden',  // Added this
      }}
    >
      {/* Scrollable container for the table */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          // Custom scrollbar styling
          '&::-webkit-scrollbar': {
            height: 10,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 5,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.primary.main, 0.3),
            borderRadius: 5,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.5),
            },
          },
        }}
      >
        {/* Table with minimum width to force scroll when needed */}
        <Box sx={{ minWidth: '100%', display: 'inline-block' }}>
          <table style={{
            borderCollapse: "collapse",
            width: "max-content",
            minWidth: "100%",
          }}>
            <thead>
              <tr>
                {columnSettings.showItemName && (
                  <th style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 10,
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    padding: "12px",
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    minWidth: 200,
                    boxShadow: '2px 0 5px rgba(0,0,0,0.1)',
                  }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      Item
                      {sortConfig.key === 'item' && (
                        sortConfig.direction === 'asc' ? <SquareArrowUp size={14} /> : <SquareArrowDown size={14} />
                      )}
                    </Box>
                  </th>
                )}
                {columnSettings.showRestaurantName && processedData.map((restaurant) => (
                  <th key={restaurant.restaurantName} style={{
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    padding: "12px",
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    minWidth: 150,
                  }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Store size={14} />
                      {restaurant.restaurantName}
                    </Box>
                  </th>
                ))}
                {columnSettings.showTotal && (
                  <th style={{
                    position: "sticky",
                    right: 0,
                    zIndex: 10,
                    backgroundColor: theme.palette.primary.dark,
                    color: "white",
                    padding: "12px",
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    minWidth: 120,
                    boxShadow: '-2px 0 5px rgba(0,0,0,0.1)',
                  }}>
                    Total
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {Object.entries(allDataForUI).map(([categoryName, categoryInfo]) => {
                const { items: categoryItems, categoryId } = categoryInfo;

                let backgroundColor = alpha(theme.palette.grey[500], 0.05);
                let borderColor = theme.palette.grey[500];
                let icon = '📦';

                if (categoryId === SWEETS_CATEGORY_ID) {
                  backgroundColor = alpha('#C70039', 0.1);
                  borderColor = '#C70039';
                  icon = '🍬';
                } else if (categoryId === SAVOURIES_CATEGORY_ID) {
                  backgroundColor = alpha('#FFB800', 0.1);
                  borderColor = '#FFB800';
                  icon = '🥟';
                }

                return (
                  <React.Fragment key={categoryName}>
                    {columnSettings.showCategory && (
                      <tr>
                        <td
                          colSpan={
                            (columnSettings.showItemName ? 1 : 0) +
                            (columnSettings.showRestaurantName ? processedData.length : 0) +
                            (columnSettings.showTotal ? 1 : 0)
                          }
                          style={{
                            backgroundColor: backgroundColor,
                            padding: "8px 12px",
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            borderLeft: `4px solid ${borderColor}`,
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            {icon} {categoryName}
                            {(categoryId === SWEETS_CATEGORY_ID || categoryId === SAVOURIES_CATEGORY_ID) && (
                              <Chip
                                label="3 Days Lead Time"
                                size="small"
                                sx={{
                                  backgroundColor: alpha(borderColor, 0.2),
                                  color: borderColor,
                                  fontSize: '0.7rem',
                                  height: 20,
                                }}
                              />
                            )}
                          </Box>
                        </td>
                      </tr>
                    )}

                    {categoryItems.map((item) => {
                      const total = processedData.reduce((sum, restaurant) => {
                        const quantity = restaurant.itemQuantities.find(q => q.title === item.title)?.quantity || 0;
                        return sum + quantity;
                      }, 0);

                      return (
                        <tr key={item.title}>
                          {columnSettings.showItemName && (
                            <td style={{
                              position: "sticky",
                              left: 0,
                              zIndex: 5,
                              padding: "12px",
                              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              backgroundColor: theme.palette.background.paper,
                              minWidth: 200,
                              boxShadow: '2px 0 5px rgba(0,0,0,0.05)',
                            }}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                                {item.title}
                              </Typography>
                            </td>
                          )}

                          {columnSettings.showRestaurantName && processedData.map((restaurant) => {
                            const quantity = restaurant.itemQuantities.find(q => q.title === item.title)?.quantity || 0;
                            return (
                              <td key={restaurant.restaurantName} style={{
                                padding: "12px",
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                backgroundColor: quantity > 0 ? alpha(theme.palette.success.main, 0.02) : "transparent",
                                textAlign: "center",
                                minWidth: 100,
                              }}>
                                {quantity > 0 ? (
                                  <Chip
                                    label={quantity}
                                    size="small"
                                    sx={{
                                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                                      color: theme.palette.success.main,
                                      fontWeight: 600,
                                      minWidth: 50,
                                    }}
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.disabled" align="center">-</Typography>
                                )}
                              </td>
                            );
                          })}

                          {columnSettings.showTotal && (
                            <td style={{
                              position: "sticky",
                              right: 0,
                              zIndex: 5,
                              padding: "12px",
                              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                              backgroundColor: theme.palette.background.paper,
                              textAlign: "center",
                              minWidth: 120,
                              boxShadow: '-2px 0 5px rgba(0,0,0,0.05)',
                            }}>
                              <Chip
                                label={total}
                                size="small"
                                sx={{
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                  color: theme.palette.primary.main,
                                  fontWeight: 600,
                                  minWidth: 50,
                                }}
                              />
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}

              {/* Grand Total Row */}
              <tr style={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                {columnSettings.showItemName && (
                  <td style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 5,
                    padding: "12px",
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    fontWeight: 600,
                    minWidth: 200,
                    boxShadow: '2px 0 5px rgba(0,0,0,0.05)',
                  }}>
                    <Typography variant="body2" fontWeight="bold">Total</Typography>
                  </td>
                )}

                {columnSettings.showRestaurantName && processedData.map((restaurant) => {
                  const grandTotal = restaurant.itemQuantities.reduce((sum, item) => sum + item.quantity, 0);
                  return (
                    <td key={restaurant.restaurantName} style={{
                      padding: "12px",
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      textAlign: "center",
                      minWidth: 100,
                    }}>
                      {grandTotal > 0 ? (
                        <Chip
                          label={grandTotal}
                          size="small"
                          sx={{
                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                            color: theme.palette.secondary.main,
                            fontWeight: 600,
                            minWidth: 50,
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.disabled" align="center">-</Typography>
                      )}
                    </td>
                  );
                })}

                {columnSettings.showTotal && (
                  <td style={{
                    position: "sticky",
                    right: 0,
                    zIndex: 5,
                    padding: "12px",
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    textAlign: "center",
                    minWidth: 120,
                    boxShadow: '-2px 0 5px rgba(0,0,0,0.05)',
                  }}>
                    <Chip
                      label={processedData.reduce((sum, restaurant) =>
                        sum + restaurant.itemQuantities.reduce((s, i) => s + i.quantity, 0), 0
                      )}
                      size="small"
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        minWidth: 50,
                      }}
                    />
                  </td>
                )}
              </tr>
            </tbody>
          </table>
        </Box>
      </Box>
    </Box>
  );

  const renderListView = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {processedData.map((restaurant) => {
        const hasOrders = restaurant.itemQuantities.some(i => i.quantity > 0);
        if (filterType === 'hasOrders' && !hasOrders) return null;
        if (filterType === 'noOrders' && hasOrders) return null;

        return (
          <Paper
            key={restaurant.restaurantName}
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              opacity: hasOrders ? 1 : 0.7,
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Checkbox
                  checked={selectedRestaurants.includes(restaurant.restaurantName)}
                  onChange={() => {
                    setSelectedRestaurants(prev =>
                      prev.includes(restaurant.restaurantName)
                        ? prev.filter(r => r !== restaurant.restaurantName)
                        : [...prev, restaurant.restaurantName]
                    );
                  }}
                  size="small"
                />
                <Typography variant="subtitle1" fontWeight="bold">
                  {restaurant.restaurantName}
                </Typography>
                {!hasOrders && (
                  <Chip label="No Orders" size="small" variant="outlined" color="default" />
                )}
              </Box>
              <Chip
                label={`${restaurant.itemQuantities.reduce((sum, i) => sum + i.quantity, 0)} items`}
                size="small"
                color={hasOrders ? "primary" : "default"}
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            <Grid container spacing={1}>
              {restaurant.itemQuantities
                .filter(i => i.quantity > 0)
                .map((item, idx) => (
                  <Grid item xs={6} sm={4} md={3} key={idx}>
                    <Box
                      sx={{
                        p: 1,
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>
                        {item.title}
                      </Typography>
                      <Chip label={item.quantity} size="small" sx={{ height: 20 }} />
                    </Box>
                  </Grid>
                ))}
            </Grid>
          </Paper>
        );
      })}
    </Box>
  );

  const renderCompactView = () => (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={getHeaderStyle()}>Restaurant</th>
            <th style={getHeaderStyle()}>Items</th>
            <th style={getHeaderStyle()}>Total Qty</th>
            <th style={getHeaderStyle()}>Status</th>
          </tr>
        </thead>
        <tbody>
          {processedData.map((restaurant) => {
            const totalQty = restaurant.itemQuantities.reduce((sum, i) => sum + i.quantity, 0);
            const hasOrders = totalQty > 0;

            return (
              <tr key={restaurant.restaurantName}>
                <td style={getCellStyle()}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Store size={16} color={theme.palette.primary.main} />
                    {restaurant.restaurantName}
                  </Box>
                </td>
                <td style={getCellStyle()}>
                  {restaurant.itemQuantities.filter(i => i.quantity > 0).length} items
                </td>
                <td style={getCellStyle()}>
                  <Chip
                    label={totalQty}
                    size="small"
                    color={hasOrders ? "primary" : "default"}
                    sx={{ minWidth: 40 }}
                  />
                </td>
                <td style={getCellStyle()}>
                  {hasOrders ? (
                    <Chip label="Active" size="small" color="success" />
                  ) : (
                    <Chip label="No Orders" size="small" variant="outlined" />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );

  // Process data function
  const processData = useCallback(async (invoices, db) => {
    try {
      const [inventoryItemsSnapshot, categoriesSnapshot, usersSnapshot] = await Promise.all([
        getDocs(collection(db, "inventoryItems")),
        getDocs(collection(db, "inventoryCategory")),
        getDocs(collection(db, "users"))
      ]);

      const inventoryItems = inventoryItemsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const categories = categoriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const categorizedItems = {};
      const uniqueItems = new Set();

      invoices.forEach(invoice => {
        invoice.items.forEach(item => {
          uniqueItems.add(item.title);
          const inventoryItem = inventoryItems.find(invItem => invItem.title === item.title);
          if (!inventoryItem) return;
          const category = categories.find(cat => cat.id === inventoryItem.categoryId);
          const categoryName = category?.category || "Uncategorized";
          if (!categorizedItems[categoryName]) categorizedItems[categoryName] = [];
          if (!categorizedItems[categoryName].some(i => i.title === item.title)) {
            categorizedItems[categoryName].push(item);
          }
        });
      });

      const itemList = Array.from(uniqueItems).map(title => ({ title }));
      const restaurantGrid = users.map(user => {
        const orders = invoices.filter(invoice =>
          invoice.userId === user.id || invoice.restaurantName === user.restaurantName
        );
        if (orders.length === 0) return null;
        const itemQuantities = itemList.map(item => {
          const matchingOrderItems = orders
            .flatMap(order => order.items)
            .filter(orderItem => orderItem.title === item.title);

          const quantity = matchingOrderItems.reduce((sum, orderItem) => sum + (Number(orderItem.quantity) || 0), 0);

          const batchCodesSet = new Set();
          matchingOrderItems.forEach(orderItem => {
            if (Array.isArray(orderItem.batchCodes)) {
              orderItem.batchCodes.forEach(code => {
                if (code) batchCodesSet.add(code);
              });
            }
          });
          const batchCodes = Array.from(batchCodesSet);

          return { title: item.title, quantity: Number(quantity), batchCodes };
        });
        return { restaurantName: user.restaurantName, itemQuantities };
      }).filter(Boolean);

      return { categorizedItems, restaurantGrid };
    } catch (err) {
      console.error("Error processing data:", err);
      throw err;
    }
  }, []);

  // Fetch data function
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const db = getFirestore();
      const { start, end } = getUKDateRange();

      const invoicesQuery = query(
        collection(db, "invoices"),
        where("createdAt", ">=", start),
        where("createdAt", "<=", end)
      );

      const unsubscribe = onSnapshot(invoicesQuery, async (snapshot) => {
        const invoices = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        if (invoices.length === 0) {
          setData([]);
          setItems({});
          setLoading(false);
          setLastUpdated(new Date());
          return;
        }

        try {
          const { categorizedItems, restaurantGrid } = await processData(invoices, db);
          setItems(categorizedItems);
          setData(restaurantGrid);
          setLastUpdated(new Date());
        } catch (err) {
          setError("Failed to process data");
          console.error(err);
        } finally {
          setLoading(false);
        }
      });

      return () => unsubscribe();
    } catch (err) {
      setError("Failed to fetch data");
      console.error(err);
      setLoading(false);
    }
  }, [processData, dateRangeType, startDate, endDate]);

  // Initial fetch
  useEffect(() => {
    const unsubscribePromise = fetchData();
    return () => {
      unsubscribePromise.then(unsubscribe => {
        if (unsubscribe && typeof unsubscribe === 'function') unsubscribe();
      });
    };
  }, [fetchData]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in={true} timeout={500}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            // borderRadius: 3,
            borderLeft: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
            <Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: theme.palette.text.primary }}>
                Today's Invoice Grid
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {processedData.length} restaurants • {Object.values(items).flat().length} items
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, val) => val && setViewMode(val)}
                size="small"
                sx={{ mr: 1 }}
              >
                <ToggleButton value="grid">
                  <Tooltip title="Grid View">
                    <Grid2X2 size={18} />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="list">
                  <Tooltip title="List View">
                    <LucideLayoutList size={18} />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="compact">
                  <Tooltip title="Compact View">
                    <TableRowsSplit size={18} />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>

              <Tooltip title="Column Settings">
                <IconButton
                  onClick={(e) => setColumnsAnchorEl(e.currentTarget)}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                >
                  <Settings size={18} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Sort">
                <IconButton
                  onClick={(e) => setSortAnchorEl(e.currentTarget)}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                >
                  <SortAsc size={18} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Filters">
                <Badge badgeContent={filterType !== 'all' ? 1 : 0} color="primary">
                  <IconButton
                    onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                    }}
                  >
                    <Filter size={18} />
                  </IconButton>
                </Badge>
              </Tooltip>

              <Tooltip title="Templates">
                <IconButton
                  onClick={() => setShowTemplates(true)}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                >
                  <Save size={18} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Export Settings">
                <IconButton
                  onClick={() => setShowExportSettings(true)}
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                >
                  <Download size={18} />
                </IconButton>
              </Tooltip>

              <Chip
                icon={<RefreshCw size={14} />}
                label={lastUpdated?.toLocaleTimeString()}
                size="small"
                variant="outlined"
                sx={{ borderRadius: 2 }}
              />

              <Tooltip title="Refresh">
                <IconButton
                  onClick={fetchData}
                  size="small"
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                  }}
                >
                  <RefreshCw size={18} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Date Range and Quick Filters */}
          <Box sx={{ mb: 3, p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.02), borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <Select
                    value={dateRangeType}
                    onChange={(e) => setDateRangeType(e.target.value)}
                    displayEmpty
                    startAdornment={<Calendar size={16} style={{ marginRight: 8 }} />}
                  >
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="yesterday">Yesterday</MenuItem>
                    <MenuItem value="last3days">Last 3 Days</MenuItem>
                    <MenuItem value="week">This Week</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {dateRangeType === 'custom' && (
                <>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={setStartDate}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={setEndDate}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </Grid>
                </>
              )}


            </Grid>
          </Box>

          {/* Restaurant Filter and Bulk Selection */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select
                  value={selectedRestaurant}
                  onChange={(e) => setSelectedRestaurant(e.target.value)}
                  displayEmpty
                  startAdornment={<Store size={16} style={{ marginRight: 8 }} />}
                >
                  <MenuItem value="all">All Restaurants</MenuItem>
                  {data.map((restaurant) => (
                    <MenuItem key={restaurant.restaurantName} value={restaurant.restaurantName}>
                      {restaurant.restaurantName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {viewMode === 'list' && (
                <Box display="flex" alignItems="center">
                  <Checkbox
                    indeterminate={selectedRestaurants.length > 0 && selectedRestaurants.length < processedData.length}
                    checked={selectedRestaurants.length === processedData.length}
                    onChange={handleSelectAll}
                    size="small"
                  />
                  <Typography variant="body2">
                    {selectedRestaurants.length} selected
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Loading State */}
          {loading && !lastUpdated && (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={400} gap={2}>
              <CircularProgress size={40} sx={{ color: theme.palette.primary.main }} />
              <Typography variant="body1" color="text.secondary">
                Loading invoice data...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Box p={2}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                <AlertTitle>Error</AlertTitle>
                {error}
                <Box mt={2}>
                  <Button variant="contained" onClick={fetchData} startIcon={<RefreshCw />}>
                    Retry
                  </Button>
                </Box>
              </Alert>
            </Box>
          )}

          {/* No Data State */}
          {!loading && !error && processedData.length === 0 && (
            <Box p={4} textAlign="center">
              <Alert severity="info" icon={<AlertCircle size={20} />}>
                <AlertTitle>No Orders Found</AlertTitle>
                No orders found for the selected date range.
              </Alert>
            </Box>
          )}

          {/* Data Display based on view mode */}
          {!loading && !error && processedData.length > 0 && (
            <>
              {viewMode === 'grid' && renderGridView()}
              {viewMode === 'list' && renderListView()}
              {viewMode === 'compact' && renderCompactView()}

              {/* Download Buttons Section */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Download Options
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3, flexWrap: "wrap" }}>

                  {/* Complete Grid Section */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" mb={1}>
                      Complete Grid (All Items)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <DownloadButton
                        onClick={downloadExcel}
                        icon={<FileSpreadsheet size={18} />}
                        label="Grid Excel"
                        loading={downloading === 'grid-excel'}
                        color="success"
                      />
                      <DownloadButton
                        onClick={downloadGridPDF}
                        icon={<FileText size={18} />}
                        label="Grid PDF"
                        loading={downloading === 'grid-pdf'}
                        color="primary"
                      />
                      <DownloadButton
                        onClick={downloadDispatchNotesExcel}
                        icon={<Truck size={18} />}
                        label="Dispatch Excel"
                        loading={downloading === 'dispatch-excel'}
                        color="warning"
                      />
                      <DownloadButton
                        onClick={downloadDispatchNotesPDF}
                        icon={<Truck size={18} />}
                        label="Dispatch PDF"
                        loading={downloading === 'dispatch-pdf'}
                        color="info"
                      />
                    </Box>
                  </Box>

                  {/* Sweets Section */}
                  {/* {sweetsData.length > 0 && (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#C70039', fontWeight: 'bold' }} display="block" mb={1}>
                        🍬 Sweets Only (3 Days Lead Time)
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <DownloadButton
                          onClick={() => downloadCategoryExcel('sweets', 'Sweets', sweetsData, sweetsItems)}
                          icon={<FileSpreadsheet size={18} />}
                          label="Sweets Excel"
                          loading={downloading === 'sweets-excel'}
                          color="success"
                          size="small"
                        />
                        <DownloadButton
                          onClick={() => downloadCategoryPDF('sweets', 'Sweets', sweetsData, sweetsItems)}
                          icon={<FileText size={18} />}
                          label="Sweets PDF"
                          loading={downloading === 'sweets-pdf'}
                          color="primary"
                          size="small"
                        />
                        <DownloadButton
                          onClick={() => downloadCategoryDispatchExcel('sweets', 'Sweets', sweetsData, sweetsItems)}
                          icon={<Truck size={18} />}
                          label="Sweets Dispatch Excel"
                          loading={downloading === 'sweets-dispatch-excel'}
                          color="warning"
                          size="small"
                        />
                        <DownloadButton
                          onClick={() => downloadCategoryDispatchPDF('sweets', 'Sweets', sweetsData, sweetsItems)}
                          icon={<Truck size={18} />}
                          label="Sweets Dispatch PDF"
                          loading={downloading === 'sweets-dispatch-pdf'}
                          color="info"
                          size="small"
                        />
                      </Box>
                    </Box>
                  )} */}

                  {/* Savouries Section */}
                  {/* {savouriesData.length > 0 && (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#FFB800', fontWeight: 'bold' }} display="block" mb={1}>
                        🥟 Savouries Only (3 Days Lead Time)
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <DownloadButton
                          onClick={() => downloadCategoryExcel('savouries', 'Savouries', savouriesData, savouriesItems)}
                          icon={<FileSpreadsheet size={18} />}
                          label="Savouries Excel"
                          loading={downloading === 'savouries-excel'}
                          color="success"
                          size="small"
                        />
                        <DownloadButton
                          onClick={() => downloadCategoryPDF('savouries', 'Savouries', savouriesData, savouriesItems)}
                          icon={<FileText size={18} />}
                          label="Savouries PDF"
                          loading={downloading === 'savouries-pdf'}
                          color="primary"
                          size="small"
                        />
                        <DownloadButton
                          onClick={() => downloadCategoryDispatchExcel('savouries', 'Savouries', savouriesData, savouriesItems)}
                          icon={<Truck size={18} />}
                          label="Savouries Dispatch Excel"
                          loading={downloading === 'savouries-dispatch-excel'}
                          color="warning"
                          size="small"
                        />
                        <DownloadButton
                          onClick={() => downloadCategoryDispatchPDF('savouries', 'Savouries', savouriesData, savouriesItems)}
                          icon={<Truck size={18} />}
                          label="Savouries Dispatch PDF"
                          loading={downloading === 'savouries-dispatch-pdf'}
                          color="info"
                          size="small"
                        />
                      </Box>
                    </Box>
                  )} */}

                  {/* Sweets & Savouries Combined Section - All 4 Options */}
                  {(sweetsData.length > 0 || savouriesData.length > 0) && (
                    <Box>
                      <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }} display="block" mb={1}>
                        🍬🥟 Sweets & Savouries Combined (All Items)
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <DownloadButton
                          onClick={downloadCombinedExcel}
                          icon={<FileSpreadsheet size={18} />}
                          label="Combined Excel"
                          loading={downloading === 'combined-excel'}
                          color="success"
                          size="small"
                        />
                        <DownloadButton
                          onClick={downloadCombinedPDF}
                          icon={<FileText size={18} />}
                          label="Combined PDF"
                          loading={downloading === 'combined-pdf'}
                          color="primary"
                          size="small"
                        />
                        <DownloadButton
                          onClick={downloadCombinedDispatchExcel}
                          icon={<Truck size={18} />}
                          label="Combined Dispatch Excel"
                          loading={downloading === 'combined-dispatch-excel'}
                          color="warning"
                          size="small"
                        />
                        <DownloadButton
                          onClick={downloadCombinedDispatchPDF}
                          icon={<Truck size={18} />}
                          label="Combined Dispatch PDF"
                          loading={downloading === 'combined-dispatch-pdf'}
                          color="info"
                          size="small"
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </>
          )}

          {/* Footer */}
          {!loading && !error && processedData.length > 0 && (
            <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Typography variant="caption" color="text.secondary">
                Showing {processedData.length} of {data.length} restaurants •
                Last updated: {lastUpdated?.toLocaleString()}
              </Typography>
            </Box>
          )}

          {/* Column Settings Popover */}
          <Popover
            open={Boolean(columnsAnchorEl)}
            anchorEl={columnsAnchorEl}
            onClose={() => setColumnsAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Box sx={{ p: 2, minWidth: 200 }}>
              <Typography variant="subtitle2" gutterBottom>
                Column Visibility
              </Typography>
              <List dense>
                <ListItem>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={columnSettings.showItemName}
                        onChange={(e) => setColumnSettings(prev => ({ ...prev, showItemName: e.target.checked }))}
                      />
                    }
                    label="Item Name"
                  />
                </ListItem>
                <ListItem>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={columnSettings.showRestaurantName}
                        onChange={(e) => setColumnSettings(prev => ({ ...prev, showRestaurantName: e.target.checked }))}
                      />
                    }
                    label="Restaurant Names"
                  />
                </ListItem>
                <ListItem>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={columnSettings.showCategory}
                        onChange={(e) => setColumnSettings(prev => ({ ...prev, showCategory: e.target.checked }))}
                      />
                    }
                    label="Category Headers"
                  />
                </ListItem>
                <ListItem>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={columnSettings.showTotal}
                        onChange={(e) => setColumnSettings(prev => ({ ...prev, showTotal: e.target.checked }))}
                      />
                    }
                    label="Total Column"
                  />
                </ListItem>
              </List>
            </Box>
          </Popover>

          {/* Sort Popover */}
          <Popover
            open={Boolean(sortAnchorEl)}
            anchorEl={sortAnchorEl}
            onClose={() => setSortAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Box sx={{ p: 2, minWidth: 200 }}>
              <Typography variant="subtitle2" gutterBottom>
                Sort By
              </Typography>
              <List dense>
                <ListItem button onClick={() => handleSort('restaurant')}>
                  <ListItemText primary="Restaurant Name" />
                  {sortConfig.key === 'restaurant' && (
                    sortConfig.direction === 'asc' ? <SquareArrowUp size={16} /> : <SquareArrowDown size={16} />
                  )}
                </ListItem>
                <ListItem button onClick={() => handleSort('totalOrders')}>
                  <ListItemText primary="Total Orders" />
                  {sortConfig.key === 'totalOrders' && (
                    sortConfig.direction === 'asc' ? <SquareArrowUp size={16} /> : <SquareArrowDown size={16} />
                  )}
                </ListItem>
              </List>
            </Box>
          </Popover>

          {/* Filter Popover */}
          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Box sx={{ p: 2, minWidth: 250 }}>
              <Typography variant="subtitle2" gutterBottom>
                Quick Filters
              </Typography>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Restaurant</InputLabel>
                <Select
                  value={selectedRestaurant}
                  onChange={(e) => setSelectedRestaurant(e.target.value)}
                  label="Restaurant"
                >
                  <MenuItem value="all">All Restaurants</MenuItem>
                  {data.map((r) => (
                    <MenuItem key={r.restaurantName} value={r.restaurantName}>
                      {r.restaurantName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={() => {
                  setSelectedRestaurant('all');
                  setFilterType('all');
                  setFilterAnchorEl(null);
                }}
              >
                Clear All Filters
              </Button>
            </Box>
          </Popover>

          {/* Templates Dialog */}
          <Dialog open={showTemplates} onClose={() => setShowTemplates(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                Saved Templates
                <IconButton onClick={() => setShowTemplates(false)} size="small">
                  <Cross size={18} />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {templates.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  No saved templates yet
                </Typography>
              ) : (
                <List>
                  {templates.map((template) => (
                    <ListItem key={template.id} divider>
                      <ListItemText
                        primary={template.name}
                        secondary={`${template.dateRangeType} • ${template.filterType} filter`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => loadTemplate(template)} size="small">
                          <Eye size={16} />
                        </IconButton>
                        <IconButton edge="end" onClick={() => deleteTemplate(template.id)} size="small">
                          <Delete size={16} />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}

              {savingTemplate ? (
                <Box sx={{ mt: 2, p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.05), borderRadius: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Template Name"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    autoFocus
                  />
                  <Box display="flex" gap={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                    <Button size="small" onClick={() => setSavingTemplate(false)}>
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={saveTemplate}
                      disabled={!templateName.trim()}
                    >
                      Save
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Plus size={16} />}
                  onClick={() => setSavingTemplate(true)}
                  sx={{ mt: 2 }}
                >
                  Save Current View as Template
                </Button>
              )}
            </DialogContent>
          </Dialog>

          {/* Export Settings Dialog */}
          <Dialog open={showExportSettings} onClose={() => setShowExportSettings(false)} maxWidth="sm" fullWidth>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                Export Preferences
                <IconButton onClick={() => setShowExportSettings(false)} size="small">
                  <Cross size={18} />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" gutterBottom>
                Include Categories
              </Typography>
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportSettings.includeSweets}
                      onChange={(e) => setExportSettings(prev => ({ ...prev, includeSweets: e.target.checked }))}
                    />
                  }
                  label="Sweets"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportSettings.includeSavouries}
                      onChange={(e) => setExportSettings(prev => ({ ...prev, includeSavouries: e.target.checked }))}
                    />
                  }
                  label="Savouries"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportSettings.includeOther}
                      onChange={(e) => setExportSettings(prev => ({ ...prev, includeOther: e.target.checked }))}
                    />
                  }
                  label="Other Categories"
                />
              </Box>

              <Typography variant="subtitle2" gutterBottom>
                Export Options
              </Typography>
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportSettings.includeSummaries}
                      onChange={(e) => setExportSettings(prev => ({ ...prev, includeSummaries: e.target.checked }))}
                    />
                  }
                  label="Include Summary Section"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={exportSettings.includeTimestamp}
                      onChange={(e) => setExportSettings(prev => ({ ...prev, includeTimestamp: e.target.checked }))}
                    />
                  }
                  label="Include Timestamp"
                />
              </Box>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Default Format</InputLabel>
                <Select
                  value={exportSettings.format}
                  onChange={(e) => setExportSettings(prev => ({ ...prev, format: e.target.value }))}
                  label="Default Format"
                >
                  <MenuItem value="excel">Excel</MenuItem>
                  <MenuItem value="pdf">PDF</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Date Format</InputLabel>
                <Select
                  value={exportSettings.dateFormat}
                  onChange={(e) => setExportSettings(prev => ({ ...prev, dateFormat: e.target.value }))}
                  label="Date Format"
                >
                  <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                  <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                  <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowExportSettings(false)}>Cancel</Button>
              <Button variant="contained" onClick={() => setShowExportSettings(false)}>
                Save Preferences
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Fade>
    </LocalizationProvider>
  );
};

export default TodayInvoicesGrid;